import {GlobalState, Glyph, GlyphState, Insight, Project, Roles, User, VisualisationAttribute, Task, HierarchicalTaskModel, IdeaBox, GlyphLayer, GlyphChange, Rule, LHS, RHS, Agent, ResourceModel, ObjectClass, IdeaBoxEntry} from "konect-model";
import ApiService from "./api-service";
import {nanoid} from "nanoid";
import InsightService from "./insight-service";
import AttributeService from "./attribute-service";
import FileSaver from "file-saver";
import UserControlService from "./user-control-service";
import {Settings} from "../view/Settings";
import LocalStorageService from "./localstorage-service";

export default class ProjectService {
    static apiService: ApiService = ApiService.getInstance();

    static async getProjectsOfUser(username: string): Promise<Project[]> {
        if (Settings.useOfflineMode) return LocalStorageService.getOfflineProjects();
        if (!username || username.length === 0) {
            console.log("No valid username provided");
            return []
        }
        try {
            await ProjectService.apiService.initAuth()
            const [insights, attributes] = await Promise.all([
                InsightService.getInsights(),
                AttributeService.getAttributes()
            ]);
            const res = await ProjectService.apiService.get(`/readUser/${username}/projects`);

            // If the API returned a numeric status code, treat as no projects (or unauthorized)
            if (!res || typeof res === 'number') {
                console.log("No projects found for user " + username);
                return [];
            }

            if (!Array.isArray(res)) {
                throw new Error("Ungültiges API-Response-Format");
            }

            const projects: Project[] = res;
            console.log("Got Projects: " + projects.length + " projects");
            return projects.map((p: Project) => this.populateProject(p, insights, attributes));

        } catch (e) {
            console.error(e);
            throw new Error("Can't load projects");
        }
    }

    static async createProject(name: string, username: string): Promise<Project | undefined> {
        let project = new Project(nanoid(), name);
        project.insights = await InsightService.getInsights();
        project.attributes = await AttributeService.getAttributes();
        if (username) {
            project.users.push({username: username, role: Roles.OWNER});
        }
        const success = this.saveProject(project);
        if (!success) return undefined;
        return project;
    }

    static async saveProject(project: Project): Promise<Boolean> {
        if (Settings.useOfflineMode) {
            LocalStorageService.saveOfflineProject(JSON.parse(this.getProjectString(project)));
            return true;
        }
        //@ts-ignore
        delete project.__v // remove versioning (quick fix for saving glyph changes, should maybe be removed later on) 
        const payload = JSON.parse(this.getProjectString(project));
        let response = await ProjectService.apiService.post(`/saveProject`, payload);
        if (response >= 400) return false;
        return true;
    }

    static exportProject(project: Project) {
        const projectString = this.getProjectString(project);
        var blob = new Blob([projectString], {type: "text/plain;charset=utf-8"});
        FileSaver.saveAs(blob, project.name + ".konect");
        UserControlService.showInfoPopup({
            title: "Export Successful",
            content: `Successfully exported project ${project.name}!`,
            duration: 5000
        });
    }

    static async importProject(file: any, user?: User) {
        const reader = new FileReader();
        const insights = await InsightService.getInsights();
        const attributes = await AttributeService.getAttributes();
        return new Promise((resolve, reject) => {
            //@ts-ignore
            reader.onload = event => resolve(event.target.result)
            reader.onerror = error => reject(error)
            reader.readAsText(file)
        }).then(content => {
            //@ts-ignore
            const project = JSON.parse(content);
            project.id = nanoid();
            !project.violations && (project.violations = [])
            //@ts-ignore
            delete project._id; // remove mongoose id as it already exists in the db
            if (user) {
                project.users = [{username: user.username, role: Roles.OWNER}];
            }
            return this.populateProject(project, insights, attributes);
        }).catch(error => console.log(error));
    }

    static async deleteProject(project: Project): Promise<any> {
        if (Settings.useOfflineMode) {
            LocalStorageService.deleteOfflineProject(project);
            return true;
        }
        return ProjectService.apiService.delete(`/deleteProject/${project.id}`);
    }

    static async updateUsers(project: Project): Promise<any> {
        return ProjectService.apiService.patch('/updateProject/users', project)
    }

    private static getProjectString(project: Project) {
        const replacer = (key: string, value: any) => {
            switch (key) {
                case "htm":
                case "mxCell":
                case "graphElement":
                case "model":
                    return;
                default:
            }
            return value;
        };
        return JSON.stringify(project, replacer);
    }

    private static populateProject(project: Project, insights: Insight[], attributes: VisualisationAttribute[]): Project {
        const fixedProject = this.fixOldProject(project, insights, attributes);
        const newP = Object.assign(new Project(), fixedProject);

        // Instantiate HierarchicalTaskModel
        if (newP.taskModel) {
            const oldTaskModel = newP.taskModel;
            newP.taskModel = new HierarchicalTaskModel();
            newP.taskModel.agents = oldTaskModel.agents?.map((a: any) => Object.assign(new Agent(), a)) || [];

            // Instantiate rootTask with all subtasks recursively
            if (oldTaskModel.rootTask) {
                newP.taskModel.rootTask = this.instantiateTask(oldTaskModel.rootTask);
            } else {
                newP.taskModel.rootTask = new Task();
            }

            // Instantiate resourceModel
            if (oldTaskModel.resourceModel) {
                newP.taskModel.resourceModel = Object.assign(new ResourceModel(), oldTaskModel.resourceModel);
                newP.taskModel.resourceModel.typeDefinition = oldTaskModel.resourceModel.typeDefinition?.map((tc: any) => {
                    const objClass = Object.assign(new ObjectClass(), tc);
                    return objClass;
                }) || [];
            }
        }

        // Instantiate Glyphs with nested layers and states
        newP.glyphs = newP.glyphs?.map((g: any) => {
            const glyph = Object.assign(new Glyph(), g);
            glyph.layers = glyph.layers?.map((l: any) => {
                const layer = Object.assign(new GlyphLayer(), l);
                layer.entries = layer.entries?.map((e: any) => Object.assign(new IdeaBoxEntry(), e)) || [];
                return layer;
            }) || [];
            glyph.states = glyph.states?.map((s: any) => {
                const state = Object.assign(new GlyphState(s.id, s.name), s);
                state.changes = state.changes?.map((c: any) => Object.assign(new GlyphChange(c.cellId, c.ignoreDuringScoring), c)) || [];
                return state;
            }) || [];
            return glyph;
        }) || [];

        // Instantiate IdeaBox
        if (newP.ideaBox) {
            newP.ideaBox = Object.assign(new IdeaBox(), newP.ideaBox);
            newP.ideaBox.entries = newP.ideaBox.entries?.map((e: any) => {
                const entry = Object.assign(new IdeaBoxEntry(), e);
                // Link entry.task to the actual Task instance in taskModel
                if (entry.task && entry.task.id && newP.taskModel && newP.taskModel.rootTask && typeof newP.taskModel.rootTask.findTask === 'function') {
                    const task = newP.taskModel.rootTask.findTask(entry.task.id);
                    if (task) entry.task = task;
                }
                return entry;
            }) || [];
        }

        // Instantiate GlobalStates
        newP.globalStates = newP.globalStates?.map((gs: any) => Object.assign(new GlobalState(gs.id, gs.name), gs)) || [];

        return newP;
    }

    private static instantiateTask(task: any): Task {
        if (!task) return new Task();

        const newTask = Object.assign(new Task(), task);

        // Recursively instantiate subtasks
        newTask.subtasks = newTask.subtasks?.map((st: any) => this.instantiateTask(st)) || [];

        // Instantiate agents
        newTask.agents = newTask.agents?.map((a: any) => Object.assign(new Agent(), a)) || [];

        // Instantiate rules with LHS and RHS
        newTask.rules = newTask.rules?.map((r: any) => {
            const rule = Object.assign(new Rule(), r);
            rule.lhs = Object.assign(new LHS(), rule.lhs);
            rule.rhs = Object.assign(new RHS(), rule.rhs);
            return rule;
        }) || [];

        return newTask;
    }

    // Used as a function to update any old projects that use older KONECT model versions. This should later be done on the server side. 
    private static fixOldProject(project: Project, insights: Insight[], attributes: VisualisationAttribute[]): Project {
        project.insights = insights; //give old projects the newest insight/attribute catalog
        project.attributes = attributes;
        !project.violations && (project.violations = [])

        if (project.globalStates && project.globalStates.length <= 0) {
            project.globalStates.push(new GlobalState("default", "default"));
        }

        for (const g of project.glyphs) {
            if (!g.states) continue;
            if (!g.states.find(s => s.id === "default")) {
                g.states.unshift(new GlyphState("default", "default"));
            }
            for (const state of g.states) {
                for (const change of state.changes) {
                    //@ts-ignore
                    if (change.geometryChange) {
                        //@ts-ignore
                        change.geometry = change.geometryChange;
                        //@ts-ignore
                        delete change.geometryChange
                    }
                    //@ts-ignore
                    if (change.styleChange) {
                        //@ts-ignore
                        change.style = change.styleChange;
                        //@ts-ignore
                        delete change.styleChange
                    }
                    //@ts-ignore
                    if (change.valueChange) {
                        //@ts-ignore
                        change.value = change.valueChange;
                        //@ts-ignore
                        delete change.valueChange
                    }
                    //@ts-ignore
                    if (change.parentChange) {
                        //@ts-ignore
                        change.parent = change.parentChange;
                        //@ts-ignore
                        delete change.parentChange;
                    }
                }
            }
        }

        return project;
    }
}